import React, { memo, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useNavigate, useParams } from 'react-router-dom';
import { AdvisorConnect } from '../AdvisorConnect/Loadable';
import { AdvisorGoals } from '../AdvisorGoals/Loadable';
import { ClientProtfolio } from 'app/pages/ClientProtfolio/Loadable';
import { usePortfolio } from 'app/Provider/PortfolioProvider';
import { useAdvisor } from 'app/Provider/AdvisorProvider';
import { MessageSquare, ChartPie, Search, Target } from 'lucide-react';

interface Props {}

export const AdvisorClientDetailPage = memo((props: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setuuid, advisorData } = useAdvisor();
  const { clientData, selectedTab, setSelectedTab } = usePortfolio();
  const prevBtn = `${process.env.REACT_APP_CDN_URL}/advisor/advisorOnboard/prevBtn.svg`;

  const handleClick = (
    tooltip: 'connect' | 'portfolio' | 'Goals' | 'Insights',
  ) => {
    setSelectedTab(tooltip);
  };

  const getColor = (
    tooltip: 'connect' | 'portfolio' | 'Insights' | 'Goals',
  ) => {
    return selectedTab === tooltip ? '#7C3EE6' : '#838EAE';
  };

  const tooltipItems = [
    { key: 'connect', text: 'Connect', IconComponent: MessageSquare },
    { key: 'portfolio', text: 'Portfolio', IconComponent: ChartPie },
    { key: 'Insights', text: 'Insights', IconComponent: Search },
    { key: 'Goals', text: 'Goals', IconComponent: Target },
  ];

  useEffect(() => {
    if (id && id !== advisorData.uuid) {
      setuuid(id);
    }
  }, [id, setuuid, advisorData.uuid]);

  return (
    <Div>
      <div className="d-flex justify-content-center position-relative">
        <PrevbtnImage
          src={prevBtn}
          alt="prevbtn"
          height={24}
          width={24}
          onClick={() => navigate(`/advisor/clients`)}
        />

        {selectedTab === 'portfolio' && (
          <ProfileWrapper className="d-flex align-items-center gap-3">
            <Circle>{clientData?.profile.full_name.charAt(0)}</Circle>
            {clientData?.profile.full_name}
          </ProfileWrapper>
        )}
        <TooltipContainer className="border d-flex justify-content-between px-5 rounded-4 rounded-top-0 bg-white">
          {tooltipItems.map((item, index) => (
            <React.Fragment key={item.key}>
              <TooltipItem
                onClick={() => handleClick(item.key as any)}
                color={getColor(item.key as any)}
                text={item.text}
                IconComponent={item.IconComponent}
              />
              {index < tooltipItems.length - 1 && (
                <div className="border-start"></div>
              )}
            </React.Fragment>
          ))}
        </TooltipContainer>
        {selectedTab === 'portfolio' && (
          <AddassetButtonWrapper>
            <AddassetButton onClick={() => navigate(`/advisor/addasset/${id}`)}>
              + Add assets or liabilities
            </AddassetButton>
          </AddassetButtonWrapper>
        )}
      </div>
      {selectedTab === 'connect' ? (
        <AdvisorConnect />
      ) : selectedTab === 'Goals' ? (
        <AdvisorGoals />
      ) : selectedTab === 'portfolio' ? (
        <ClientProtfolio />
      ) : null}
    </Div>
  );
});

const PrevbtnImage = styled.img`
  position: absolute;
  top: 20%;
  left: 1.2%;
  cursor: pointer;
`;

const ProfileWrapper = styled.div`
  position: absolute;
  top: 20%;
  left: 5%;
  cursor: pointer;
`;

const AddassetButtonWrapper = styled.div`
  position: absolute;
  top: 20%;
  right: 3%;
`;

const Div = styled.div`
  background-color: #f0f2f8;
`;

const TooltipContainer = styled.div`
  height: 52px;
  width: 725px;
`;

interface TooltipItemProps {
  onClick: () => void;
  color: string;
  text: string;
  IconComponent: React.ElementType;
}

const TooltipItem: React.FC<TooltipItemProps> = ({
  onClick,
  color,
  text,
  IconComponent,
}) => (
  <div
    className="d-flex gap-3 justify-content-center align-items-center cursor-pointer"
    onClick={onClick}
  >
    <IconComponent color={color} size={16} />
    <TooltipText style={{ color }} className="my-1">
      {text}
    </TooltipText>
  </div>
);

const TooltipText = styled.h4`
  font-weight: 700;
  font-size: 16px;
  color: #838eae;
`;

const AddassetButton = styled.button`
  width: auto;
  background-color: #8276f4;
  color: #f3f1fe;
  font-weight: 400;
  font-size: 12px;
  border: none;
  padding: 10px 30px;
  border-radius: 30px;
  height: 36px;
  cursor: pointer;

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const Circle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 27px;
  height: 27px;
  border: 1px solid rgb(245, 245, 248);
  border-radius: 50%;
  cursor: pointer;
  background-color: #838eae;
  font-size: 14px;
  font-weight: 500;
  color: white;
`;
